import React, { useContext, useEffect, useState } from 'react'
import { timeAgo } from '../../../utils'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import { AuthContext } from '../../../context/AuthContext'
import { createPortal } from 'react-dom'
import { AddMember } from './addMember'
import { UserAvatarPlaceholder } from '../../icons/UserAvatarPlaceholder'
import { Member } from './member'
import toast from 'react-hot-toast'

export function Team() {
  const [isLoading, setIsLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [showInviteMemberDialog, setShowInviteMemberDialog] = useState(false)
  const { user } = useContext(AuthContext)
  const customFetch = useCustomFetch()

  async function handleDeleteMember(id, name) {
    if (!id) return
    try {
      const url = name ? `/deleteMember/${id}` : `/deleteInvite/${id}`
      await customFetch(url, 'DELETE', null)
      toast.success('Member deleted successfully')
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id))
    } catch (error) {
      console.error('error deleting member', error)
      toast.error('Error deleting member')
    }
  }

  async function getUsersByWorkspace() {
    try {
      let response = await customFetch(`/getUsersByWorkspace/${user.active_workspace}`, 'GET', null)
      const usersRes = response.users.map((member) => {
        return {
          id: member.id,
          email: member.email,
          name: member.name,
          picture: member.picture,
          last_seen: member.last_seen,
          role: member.workspace.role,
          invited_at: member.workspace.invited_at,
          joined_at: member.workspace.joined_at,
        }
      })
      response = await customFetch(`/getInvitesByWorkspace/${user.active_workspace}`, 'GET', null)
      const invitesRes = response.users.map((invite) => {
        return {
          id: invite.id,
          email: invite.email,
          invited_at: invite.created_at,
        }
      })
      const members = [...usersRes, ...invitesRes]
      members.sort((a, b) => b.invited_at - a.invited_at)
      setUsers(members)
    } catch (error) {
      console.error('error searching users', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (typeof user.active_workspace === 'undefined') return
    getUsersByWorkspace()
  }, [user.active_workspace])

  return (
    <div className="bg-grey-100 dark:bg-gray-800 min-h-screen pr-[102px] pt-[36px]">
      <div className="mb-[36px] flex justify-between items-center">
        <div>
          <p className="text-[#40424A] dark:text-gray-100 text-[22px] font-medium font-['DM_Sans'] leading-relaxed">
            Team Members
          </p>
          <p className="text-slate dark:text-gray-400 text-lg font-normal font-['DM_Sans'] leading-[23px]">
            Members can schedule & lead interviews, edit & share projects they're on.
          </p>
        </div>
        <button
          className="w-[143px] h-9 px-3 py-2 mr-[3px] bg-[#FF4A8E] rounded shadow-inner justify-center items-center gap-[3px] inline-flex"
          onClick={() => setShowInviteMemberDialog(true)}
        >
          <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_2862_9560)">
              <path
                d="M10 4.66602V16.3327"
                stroke="white"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.16699 10.5H15.8337"
                stroke="white"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_2862_9560">
                <rect width="20" height="20" fill="white" transform="translate(0 0.5)" />
              </clipPath>
            </defs>
          </svg>
          <p className="text-white text-base font-semibold font-['Manrope'] leading-tight">Add Member</p>
        </button>
        {showInviteMemberDialog &&
          createPortal(
            <AddMember setUsers={setUsers} handleClose={() => setShowInviteMemberDialog(false)} />,
            document.body
          )}
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow pt-2 pb-8">
        {isLoading && (
          <div className="flex justify-center items-center">
            <svg className="w-40 h-40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
              <radialGradient id="a1" cx=".66" fx=".66" cy=".3125" fy=".3125" gradientTransform="scale(1.5)">
                <stop offset="0" stopColor="#FF156D"></stop>
                <stop offset=".3" stopColor="#FF156D" stopOpacity=".9"></stop>
                <stop offset=".6" stopColor="#FF156D" stopOpacity=".6"></stop>
                <stop offset=".8" stopColor="#FF156D" stopOpacity=".3"></stop>
                <stop offset="1" stopColor="#FF156D" stopOpacity="0"></stop>
              </radialGradient>
              <circle
                transform-origin="center"
                fill="none"
                stroke="url(#a1)"
                strokeWidth="15"
                strokeLinecap="round"
                strokeDasharray="200 1000"
                strokeDashoffset="0"
                cx="100"
                cy="100"
                r="70"
              >
                <animateTransform
                  type="rotate"
                  attributeName="transform"
                  calcMode="spline"
                  dur="2"
                  values="360;0"
                  keyTimes="0;1"
                  keySplines="0 0 1 1"
                  repeatCount="indefinite"
                ></animateTransform>
              </circle>
              <circle
                transform-origin="center"
                fill="none"
                opacity=".2"
                stroke="#FF156D"
                strokeWidth="15"
                strokeLinecap="round"
                cx="100"
                cy="100"
                r="70"
              ></circle>
            </svg>
          </div>
        )}
        {!isLoading &&
          users.map((member) => (
            <Member
              key={member.id}
              member={member}
              handleDeleteMember={() => handleDeleteMember(member.id, member.name)}
            />
          ))}
      </div>
    </div>
  )
}
